import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { JobDescriptionTemplate } from 'src/app/models/job_description_template.model';
import { JobVacancyPhaseTypeEnum, JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { JobVacancy, JobVacancyBody } from 'src/app/models/job_vacancy.model';
import { ApiService, TinymceService } from 'src/app/services';

const DEFAULT_JOB_VACANCY_PHASE_TYPES: JobVacancyPhaseTypeEnum[] = [
  JobVacancyPhaseTypeEnum.Prescreening,
  JobVacancyPhaseTypeEnum.Screening,
  JobVacancyPhaseTypeEnum.Technical,
  JobVacancyPhaseTypeEnum.Topgrading,
  JobVacancyPhaseTypeEnum.Offer,
];

export interface JobVacancyFormDialogData {
  mode: 'create' | 'edit';
  job_vacancy?: JobVacancy;
}

@Component({
  selector: 'app-job-vacancy-form-dialog',
  templateUrl: './job-vacancy-form-dialog.component.html',
  styleUrl: './job-vacancy-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    EditorComponent,
  ],
})
export class JobVacancyFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<JobVacancyFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly tinymce_service = inject(TinymceService);

  readonly data = inject<JobVacancyFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly department_categories = signal<DepartmentCategory[]>([]);
  readonly job_description_templates = signal<JobDescriptionTemplate[]>([]);
  readonly is_edit_mode = this.data.mode === 'edit';

  tiny_mce_api_key = '';

  readonly tiny_mce_init = {
    statusbar: false,
    menubar: false,
    height: '35vh',
    ui_mode: 'split' as const,
    plugins: 'lists link table wordcount',
    toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | link table | removeformat',
  };

  readonly job_vacancy_phase_type_options = DEFAULT_JOB_VACANCY_PHASE_TYPES.map(phase_type => ({
    value: phase_type,
    label: JobVacancyPhaseTypeEnumLabels[phase_type],
  }));

  readonly form = this.form_builder.group({
    title: [this.data.job_vacancy?.title ?? '', [Validators.required, Validators.maxLength(255)]],
    offer_url: [this.data.job_vacancy?.offer_url ?? ''],
    job_description: [this.data.job_vacancy?.job_description ?? ''],
    start_date: [this.parseDate(this.data.job_vacancy?.start_date) ?? new Date(), Validators.required],
    end_date: [this.parseDate(this.data.job_vacancy?.end_date)],
    department_category_ids: [
      this.data.job_vacancy?.department_categories?.map(department_category => department_category.id) ?? ([] as number[]),
    ],
    job_description_template_id: [null as number | null],
    job_vacancy_phase_types: [this.resolveInitialJobVacancyPhaseTypes(), [Validators.required, Validators.minLength(1)]],
  });

  ngOnInit(): void {
    this.tiny_mce_api_key = this.tinymce_service.getKey();

    this.api_service
      .getDepartmentCategories({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<DepartmentCategory> | DepartmentCategory[]): void => {
          const department_categories = Array.isArray(response) ? response : response.data;
          this.department_categories.set(department_categories);
        },
      });

    this.api_service
      .getJobDescriptionTemplates({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<JobDescriptionTemplate> | JobDescriptionTemplate[]): void => {
          const templates = Array.isArray(response) ? response : response.data;
          this.job_description_templates.set(templates);
        },
      });
  }

  importJobDescriptionTemplate(): void {
    const template_id = this.form.get('job_description_template_id')?.value;

    if (template_id === null) {
      return;
    }

    const job_description_template = this.job_description_templates().find(template => template.id === template_id);

    if (job_description_template === undefined) {
      return;
    }

    this.form.patchValue({ job_description: job_description_template.html_body });
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: JobVacancyBody = {
      title: raw.title ?? '',
      offer_url: raw.offer_url || null,
      job_description: raw.job_description || null,
      start_date: this.formatDate(raw.start_date),
      end_date: this.formatDate(raw.end_date) || null,
      department_category_ids: raw.department_category_ids ?? [],
      job_vacancy_phase_types: this.sortJobVacancyPhaseTypes(raw.job_vacancy_phase_types ?? []),
    };

    const request$ =
      this.is_edit_mode && this.data.job_vacancy
        ? this.api_service.updateJobVacancy(this.data.job_vacancy.id, body)
        : this.api_service.createJobVacancy(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => {
        this.dialog_ref.close(true);
      },
      error: (err: { error?: { error?: string } }): void => {
        this.loading.set(false);
        const message = err?.error?.error ?? 'Error al guardar la vacante.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }

  private sortJobVacancyPhaseTypes(job_vacancy_phase_types: JobVacancyPhaseTypeEnum[]): JobVacancyPhaseTypeEnum[] {
    return DEFAULT_JOB_VACANCY_PHASE_TYPES.filter(phase_type => job_vacancy_phase_types.includes(phase_type));
  }

  private resolveInitialJobVacancyPhaseTypes(): JobVacancyPhaseTypeEnum[] {
    const job_vacancy_phases = this.data.job_vacancy?.job_vacancy_phases;

    if (job_vacancy_phases === undefined || job_vacancy_phases.length === 0) {
      return [...DEFAULT_JOB_VACANCY_PHASE_TYPES];
    }

    return [...job_vacancy_phases]
      .sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order)
      .map(job_vacancy_phase => job_vacancy_phase.type);
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(part => parseInt(part, 10));

    return new Date(year, month - 1, day);
  }

  private formatDate(value: Date | null | undefined): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
